/**
 * History — an editor-owned undo/redo command stack for scene element changes
 * (transforms, adds, removes). Engine-agnostic: it stores immutable snapshots of
 * the editor's authoritative `ElementState[]` and is driven by the Editor at its
 * commit points. It has NO dependency on the web app — the Editor exposes
 * undo()/redo() and emits the restored state so any host (web Vuex, standalone)
 * can mirror it.
 *
 * Snapshots cover only `elements` (the panels/models in the scene); scenes are
 * not edited by the undoable actions, so they are intentionally excluded.
 */

import type { ElementState } from './EditorState';

export interface HistorySnapshot {
  elements: ElementState[];
}

/** Deep-clone an element list so snapshots never alias live state. */
function cloneElements(elements: ElementState[]): ElementState[] {
  return elements.map((el) => ({
    sceneID: el.sceneID,
    assetID: el.assetID,
    parentID: el.parentID,
    position: [...el.position],
    rotation: [...el.rotation],
    scale: [...el.scale],
  }));
}

function arraysEqual(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * Order-insensitive structural equality of two element lists (keyed by sceneID).
 * Used to dedup no-op changes — e.g. the echo when a committed transform
 * round-trips back through the host, or the initial-load re-sync — so they don't
 * create spurious history entries. Object key order is ignored (the host and the
 * editor may build element objects with different key orders).
 */
export function elementsEqual(a: ElementState[], b: ElementState[]): boolean {
  if (a.length !== b.length) return false;
  const byId = new Map(b.map((e) => [e.sceneID, e]));
  for (const x of a) {
    const y = byId.get(x.sceneID);
    if (!y) return false;
    if (x.assetID !== y.assetID || x.parentID !== y.parentID) return false;
    if (
      !arraysEqual(x.position, y.position) ||
      !arraysEqual(x.rotation, y.rotation) ||
      !arraysEqual(x.scale, y.scale)
    ) {
      return false;
    }
  }
  return true;
}

export class History {
  private past: HistorySnapshot[] = [];
  private future: HistorySnapshot[] = [];
  private readonly limit: number;

  constructor(limit = 100) {
    this.limit = limit;
  }

  /**
   * Record the state that exists BEFORE a change is applied. Pushing a new change
   * invalidates the redo stack. Call this right before mutating `elements`.
   */
  record(currentElements: ElementState[]): void {
    this.past.push({ elements: cloneElements(currentElements) });
    if (this.past.length > this.limit) this.past.shift();
    this.future = [];
  }

  /**
   * Move one step back. `currentElements` (the live state) is pushed to the redo
   * stack and the previous snapshot is returned, or null if nothing to undo.
   */
  undo(currentElements: ElementState[]): HistorySnapshot | null {
    const snapshot = this.past.pop();
    if (!snapshot) return null;
    this.future.push({ elements: cloneElements(currentElements) });
    return snapshot;
  }

  /** Move one step forward. Returns the next snapshot, or null if nothing to redo. */
  redo(currentElements: ElementState[]): HistorySnapshot | null {
    const snapshot = this.future.pop();
    if (!snapshot) return null;
    this.past.push({ elements: cloneElements(currentElements) });
    return snapshot;
  }

  get canUndo(): boolean {
    return this.past.length > 0;
  }

  get canRedo(): boolean {
    return this.future.length > 0;
  }

  /** Drop all history (e.g. when a new scene is loaded). */
  clear(): void {
    this.past = [];
    this.future = [];
  }
}

export default History;
